<template>
    <div class="onboarding-tour-parent">
        <VOnboardingWrapper ref="wrapper" :steps="steps" :options="options">
            <template #default="{ previous, step, isFirst, index }">
                <VOnboardingStep>
                    <div
                        v-if="step.content && !isLoading && focusTrapReady"
                        class="modal-card onboarding-tooltip"
                        tabindex="0"
                    >
                        <div class="modal-card-head columns">
                            <span class="column tour-dropdown-content-title modal-card-title">
                                {{ step.content.title }}
                            </span>
                            <span class="column is-2 tour-dropdown-content-title" style="text-align: right">
                                <button type="button" class="delete" @click="skipOnboarding" />
                            </span>
                        </div>

                        <section class="modal-card-body">
                            <p v-if="steps.length" class="tour-steps">Step {{ `${index + 1} of ${steps.length}` }}</p>
                            <p class="tour-tooltip-body" v-html="step.content.body" />
                        </section>

                        <footer v-if="!step.content.mustClick" class="modal-card-foot">
                            <e-button v-if="!isFirst" @click="previous">
                                {{ step.content.settings?.buttons?.previous ?? 'Previous' }}
                            </e-button>
                            <e-button v-if="index + 1 === steps.length" type="is-primary" @click="exitOnboarding">
                                {{ step.content.settings?.buttons?.finish ?? 'Finish' }}
                            </e-button>
                            <e-button v-else type="is-primary" @click="listenToNextStep(index)">
                                {{ step.content.settings?.buttons?.next ?? 'Next' }}
                            </e-button>
                        </footer>
                    </div>
                </VOnboardingStep>
            </template>
        </VOnboardingWrapper>
    </div>
</template>

<script>
import { useVOnboarding, VOnboardingStep, VOnboardingWrapper } from 'v-onboarding';
import { defineComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

export default defineComponent({
    components: { VOnboardingWrapper, VOnboardingStep },
    props: {
        activeTour: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['close-tour'],

    setup(props, { emit }) {
        const route = useRoute();
        const wrapper = ref(null);
        const currentStepIndex = ref(0);
        const { start, goToStep, finish } = useVOnboarding(wrapper, {});
        const isLoading = ref(false);
        const focusTrapReady = ref(false);

        const steps = props.activeTour.tour.tips.map((step, index) => ({
            attachTo: { element: step.dom_element_identifier },
            content: {
                title: step.title,
                body: step.body,
                mustClick: step.call_to_action,
                settings: step.settings,
            },
            hideButtons: {
                previous: props.activeTour.tour.tips[index - 1]?.call_to_action,
            },
            options: {
                popper: {
                    placement: step.placement || 'auto',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: step.settings?.offset || [0, 10],
                            },
                        },
                    ],
                },
                overlay: {
                    padding: step.settings?.padding || 5,
                },
            },
        }));

        const options = ref({
            overlay: {
                enabled: true,
                padding: 5,
                borderRadius: 10,
                preventOverlayInteraction: false,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
            scrollToStep: {
                enabled: true,
                options: {
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center',
                },
            },
        });

        // Function to enable pointer events
        const enablePointerEvents = () => {
            document.body.classList.remove('tour-active');
        };

        // Function to remove event listener
        const removeEventListener = index => {
            const stepId = props.activeTour.tour.tips[index].dom_element_identifier;
            const element = document.querySelector(stepId);
            if (element) {
                // Use a more generic approach to remove listeners
                const newElement = element.cloneNode(true);
                element.parentNode.replaceChild(newElement, element);
            }
        };

        // Function to exit onboarding
        const exitOnboarding = () => {
            enablePointerEvents();
            removeEventListener(currentStepIndex.value);
            finish();
            emit('close-tour');
        };

        // Define listenToNextStep function
        const listenToNextStep = index => {
            if (index >= 1 && currentStepIndex.value !== index) {
                currentStepIndex.value = index;
            }

            if (currentStepIndex.value < steps.length - 1) {
                isLoading.value = true;
                currentStepIndex.value++;
                const newStep = props.activeTour.tour.tips[currentStepIndex.value];
                const selector = newStep.dom_element_identifier;

                setTimeout(() => {
                    const element = document.querySelector(selector);
                    if (element) {
                        if (newStep.call_to_action) {
                            element.style.pointerEvents = 'auto';
                            element.addEventListener('click', () => listenToNextStep(index));
                        } else {
                            removeEventListener(currentStepIndex.value);
                        }
                        goToStep(currentStepIndex.value);
                        isLoading.value = false;
                    }
                }, 200);
            } else {
                exitOnboarding();
            }
        };

        const skipOnboarding = () => {
            enablePointerEvents();
            removeEventListener(currentStepIndex.value);
            finish();
        };

        onMounted(() => {
            if (wrapper.value) {
                document.body.classList.add('tour-active');
                nextTick(() => {
                    setTimeout(() => {
                        wrapper.value.start();
                        focusTrapReady.value = true;
                        const firstStepId = props.activeTour.tour.tips[0].dom_element_identifier;
                        const element = document.querySelector(firstStepId);
                        if (element) {
                            element.style.pointerEvents = 'auto';
                            element.addEventListener('click', listenToNextStep);
                        }
                    }, 100);
                });
            }
        });

        watch(
            () => route.path,
            () => {
                enablePointerEvents();
                removeEventListener(currentStepIndex.value);
                currentStepIndex.value = 0;
                finish();
            }
        );

        onUnmounted(() => {
            enablePointerEvents();
            removeEventListener(currentStepIndex.value);
            finish();
        });

        return {
            options,
            isLoading,
            listenToNextStep,
            exitOnboarding,
            skipOnboarding,
            wrapper,
            steps,
            start,
            finish,
            goToStep,
            currentStepIndex,
            focusTrapReady,
        };
    },
});
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.onboarding-tooltip {
    max-width: 300px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    position: relative;
    font-size: 0.875rem;
    border-radius: 0.5rem !important;

    .modal-card-head {
        padding: 1rem 1rem !important;
        padding-bottom: 0 !important;
        margin-bottom: unset !important;
        background: $white;

        .modal-card-title {
            font-size: $size-8;
            font-weight: $weight-bold;
            text-align: left;
            color: $black;
        }
    }

    .modal-card-body {
        padding: 1rem;
        background: $white;
        color: $black;
        .tour-steps {
            font-size: 0.75rem;
            color: #ccc;
        }
    }

    .modal-card-foot {
        font-size: $size-8;
        font-weight: $weight-bold;
        text-align: left;
        background: $white;
        color: $black;
    }
}

.tour-active {
    pointer-events: none !important;

    .is-clickable {
        cursor: pointer !important;
        pointer-events: none !important;
    }

    #submissionPoints {
        pointer-events: none !important;
        /* cursor should be the pointer */
    }
}
</style>
